import fuzzy from 'fuzzy';
import _ from 'lodash';

import {KeyboardActionsEnum} from '../../../../libs/shared/core/src/helpers/browser/keyboardHelpers';
import {LocalizeRenderer} from '../../../../libs/shared/core/src/helpers/internationalization/internationalizationContext';
import {defineMessage} from '../../../../libs/shared/core/src/helpers/internationalization/message';
import {InboxModel} from '../../../../libs/shared/core/src/models/api/inboxModel';
import {TeammateModel} from '../../../../libs/shared/core/src/models/api/teammateModel';
import {TeamModel} from '../../../../libs/shared/core/src/models/api/teamModel';
import {makeEnumTypeGuard} from '../../../../libs/shared/core/src/models/api/types/enumTypes';
import {AppContentTypesEnum} from '../../../../libs/shared/core/src/store/panels/app/commonAppPanelState';
import {IconName} from '../../../../libs/shared/ui/src/components/icon/icon';
import {VisualCustomView} from '../../../helpers/models/customViewHelpers';
import {LensModel} from '../../../models/api/lensModel';
import {PreferencesModel} from '../../../models/api/preferencesModel';
import {TeammateOnlineStatusesEnum} from '../../../store/data/teammates/onlineTeammatesSelectors';
import {SidePanelFrame} from '../../../store/stateless/sidePanelFrameSelectors';
import {Filter} from '../cells/header/cellsHeaderFilters';

export enum CommandPaletteItemTypesEnum {
  ROUTE = 'ROUTE',
  INBOX = 'INBOX',
  TEAMMATE = 'TEAMMATE',
  TEAM = 'TEAM',
  CUSTOM_VIEW = 'CUSTOM_VIEW',
  LENS = 'LENS',
  FRAME = 'FRAME',
  FILTER = 'FILTER',
  KEYBOARD_ACTION = 'KEYBOARD_ACTION',
  PREFERENCE_ACTION = 'PREFERENCE_ACTION',
  REDUX_ACTION = 'REDUX_ACTION',
  APP = 'APP',
}

export const isCommandPaletteItemType = makeEnumTypeGuard(CommandPaletteItemTypesEnum);

export enum CommandPaletteCategoriesEnum {
  INTEGRATIONS = 'integrations',
  ASSIGNED_CONVERSATIONS = 'assignedConversations',
  MY_INBOX = 'myInbox',
  TAGS = 'tags',
  LENSES = 'lenses',
  FILTERS = 'filters',
  UNREAD = 'unread',
  TEAMS = 'teams',
  FRONT_APPS = 'frontapp',
  PREFERENCES = 'preferences',
}
export const isCommandPaletteCategory = makeEnumTypeGuard(CommandPaletteCategoriesEnum);

const commandPaletteCategoriesBangs: {
  [k in CommandPaletteCategoriesEnum | CommandPaletteItemTypesEnum]?: Array<string>;
} = {
  [CommandPaletteCategoriesEnum.MY_INBOX]: ['!m'],
  [CommandPaletteCategoriesEnum.TAGS]: ['!tags', '!ta', '!t'],
  [CommandPaletteCategoriesEnum.FILTERS]: ['!filters', '!f'],
  [CommandPaletteCategoriesEnum.ASSIGNED_CONVERSATIONS]: ['!ac', '!c'],
  [CommandPaletteCategoriesEnum.INTEGRATIONS]: ['!int', '!integrations'],
  [CommandPaletteCategoriesEnum.FRONT_APPS]: ['!apps', '!fa', '!a'],
  [CommandPaletteCategoriesEnum.LENSES]: ['!views', '!v'],
  [CommandPaletteCategoriesEnum.PREFERENCES]: ['!pref', '!preferences'],

  [CommandPaletteItemTypesEnum.INBOX]: ['!i'],
};

export function findCategoryOrItemTypeForBang(
  prefix: string,
): CommandPaletteCategoriesEnum | CommandPaletteItemTypesEnum | undefined {
  const maybeCategoryOrType = _(commandPaletteCategoriesBangs)
    .toPairs()
    .find(([, prefixes]) => prefixes.includes(prefix))?.[0];

  if (!isCommandPaletteCategory(maybeCategoryOrType) && !isCommandPaletteItemType(maybeCategoryOrType)) {
    return undefined;
  }
  return maybeCategoryOrType;
}
export const possiblePaletteCategoriesBangs = _(commandPaletteCategoriesBangs)
  .values()
  .flatten()
  .compact()
  .orderBy((i) => i.length, 'desc')
  .value();
const bangsRegex = new RegExp(`^(${possiblePaletteCategoriesBangs.join('|')})`, 'i');

export const commandPaletteCategoriesRenderer: Record<CommandPaletteCategoriesEnum, LocalizeRenderer> = {
  [CommandPaletteCategoriesEnum.UNREAD]: (localize) =>
    localize(
      defineMessage({
        id: 'palette-category-unread',
        message: 'Unread',
      }),
    ),
  [CommandPaletteCategoriesEnum.MY_INBOX]: (localize) =>
    localize(
      defineMessage({
        id: 'palette-category-myinbox',
        message: 'Me',
      }),
    ),
  [CommandPaletteCategoriesEnum.TAGS]: (localize) =>
    localize(
      defineMessage({
        id: 'palette-category-tags',
        message: 'Tags',
      }),
    ),
  [CommandPaletteCategoriesEnum.FILTERS]: (localize) =>
    localize(
      defineMessage({
        id: 'palette-category-filters',
        message: 'Filters',
      }),
    ),
  [CommandPaletteCategoriesEnum.TEAMS]: (localize) =>
    localize(
      defineMessage({
        id: 'palette-category-teams',
        message: 'Shared inboxes',
      }),
    ),
  [CommandPaletteCategoriesEnum.ASSIGNED_CONVERSATIONS]: (localize) =>
    localize(
      defineMessage({
        id: 'palette-category-assignedconversations',
        message: 'Assigned conversations',
      }),
    ),
  [CommandPaletteCategoriesEnum.INTEGRATIONS]: (localize) =>
    localize(
      defineMessage({
        id: 'palette-category-integrations',
        message: 'Integrations',
      }),
    ),
  [CommandPaletteCategoriesEnum.PREFERENCES]: (localize) =>
    localize(
      defineMessage({
        id: 'palette-category-preferences',
        message: 'Preferences',
      }),
    ),
  [CommandPaletteCategoriesEnum.FRONT_APPS]: (localize) =>
    localize(
      defineMessage({
        id: 'palette-category-frontapps',
        message: 'Front apps',
      }),
    ),
  [CommandPaletteCategoriesEnum.LENSES]: (localize) =>
    localize(
      defineMessage({
        id: 'palette-category-lenses',
        message: 'Views',
      }),
    ),
};
export const commandPaletteCategoriesOrders = {
  [CommandPaletteCategoriesEnum.UNREAD]: 100,
  [CommandPaletteCategoriesEnum.MY_INBOX]: 90,
  [CommandPaletteCategoriesEnum.TAGS]: 80,
  [CommandPaletteCategoriesEnum.LENSES]: 75,
  [CommandPaletteCategoriesEnum.FILTERS]: 70,
  [CommandPaletteCategoriesEnum.TEAMS]: 60,
  [CommandPaletteCategoriesEnum.ASSIGNED_CONVERSATIONS]: 50,
  [CommandPaletteCategoriesEnum.INTEGRATIONS]: 40,
  [CommandPaletteCategoriesEnum.FRONT_APPS]: 30,
};

export interface CommandPaletteItemBase<T extends CommandPaletteItemTypesEnum> {
  type: T;
  itemId: string | number;
  label: string;
  isBold?: boolean;
  keyboardAction?: KeyboardActionsEnum;
  parentCategoryId?: CommandPaletteCategoriesEnum | string;
  score?: number;
}

export interface CommandPaletteFilterItem extends CommandPaletteItemBase<CommandPaletteItemTypesEnum.FILTER> {
  filter: Filter;
  icon: IconName;
}

export interface CommandPaletteTeammateItem
  extends CommandPaletteItemBase<CommandPaletteItemTypesEnum.TEAMMATE> {
  teammate: TeammateModel;
  path: string;
  onlineStatus: TeammateOnlineStatusesEnum;
  isPinned: boolean;
}

export interface CommandPaletteAppItem extends CommandPaletteItemBase<CommandPaletteItemTypesEnum.APP> {
  contentType: AppContentTypesEnum;
  icon: IconName;
}

export interface CommandPaletteRouteItem extends CommandPaletteItemBase<CommandPaletteItemTypesEnum.ROUTE> {
  path: string;
  icon: IconName;
  counterValue?: number;
  counterKey?: string;
}

export interface CommandPaletteInboxItem extends CommandPaletteItemBase<CommandPaletteItemTypesEnum.INBOX> {
  inbox: InboxModel;
  counterValue?: number;
  path: string;
  isPinned: boolean;
}

export interface CommandPaletteCustomViewItem
  extends CommandPaletteItemBase<CommandPaletteItemTypesEnum.CUSTOM_VIEW> {
  customView: VisualCustomView;
  path: string;
}

export interface CommandPaletteLensItem extends CommandPaletteItemBase<CommandPaletteItemTypesEnum.LENS> {
  lens: LensModel;
  path: string;
}

export interface CommandPaletteTeamItem extends CommandPaletteItemBase<CommandPaletteItemTypesEnum.TEAM> {
  team: TeamModel;
  path: string;
}

export interface CommandPaletteFrameItem extends CommandPaletteItemBase<CommandPaletteItemTypesEnum.FRAME> {
  frame: SidePanelFrame;
}

export interface CommandPaletteKeyboardActionItem
  extends CommandPaletteItemBase<CommandPaletteItemTypesEnum.KEYBOARD_ACTION> {
  icon: IconName;
}

export interface CommandPalettePreferenceActionItem
  extends CommandPaletteItemBase<CommandPaletteItemTypesEnum.PREFERENCE_ACTION> {
  icon: IconName;
  preferencePatch: Partial<PreferencesModel>;
}

export enum CommandPaletteReduxActionsEnum {
  CLEAR_OFFLINE_CACHE = 'CLEAR_OFFLINE_CACHE',
}

export interface CommandPaletteReduxActionItem
  extends CommandPaletteItemBase<CommandPaletteItemTypesEnum.REDUX_ACTION> {
  reduxAction: CommandPaletteReduxActionsEnum;
  icon: IconName;
}

export type CommandPaletteItem =
  | CommandPaletteCustomViewItem
  | CommandPaletteLensItem
  | CommandPaletteFilterItem
  | CommandPaletteFrameItem
  | CommandPaletteKeyboardActionItem
  | CommandPalettePreferenceActionItem
  | CommandPaletteReduxActionItem
  | CommandPaletteInboxItem
  | CommandPaletteRouteItem
  | CommandPaletteTeamItem
  | CommandPaletteAppItem
  | CommandPaletteTeammateItem;

function getCategoryFromQueryBang(query: string) {
  const maybeMatchBang = query.match(bangsRegex);

  // If no prefixes are found, proceed like normal.
  if (!maybeMatchBang) {
    return undefined;
  }

  const [, bang] = maybeMatchBang;

  // If the prefix doesn't match one of ours, proceed like normal.
  if (!possiblePaletteCategoriesBangs.includes(bang.toLowerCase())) {
    return undefined;
  }

  const categoryOrTypeToMatch = findCategoryOrItemTypeForBang(bang.toLowerCase());

  // If the prefix doesn't match a category, proceed like normal.
  if (!categoryOrTypeToMatch) {
    return undefined;
  }

  return {bang, categoryOrTypeToMatch};
}

export function fuzzySearchPalette(
  list: ReadonlyArray<CommandPaletteItem>,
  query: string,
  showScores: boolean,
): ReadonlyArray<CommandPaletteItem> {
  const bangData = getCategoryFromQueryBang(query);
  const bang = bangData?.bang;
  const queryWithoutBang = !bang ? query : query.slice(bang.length + 1);
  const isBangOnly = Boolean(bang && queryWithoutBang.trim().length === 0);

  return _(list)
    .filter((item) => 'label' in item)
    .filter((item) => {
      if (!bangData?.categoryOrTypeToMatch) {
        return true;
      }

      if (isCommandPaletteCategory(bangData.categoryOrTypeToMatch)) {
        return item.parentCategoryId === bangData.categoryOrTypeToMatch;
      }

      return item.type === bangData.categoryOrTypeToMatch;
    })
    .map((item) => {
      // 1. Construct a string to match on
      const string = item.label;

      // 2. Match the string with prefix
      const matchOnBaseString = fuzzy.match(queryWithoutBang, string);
      let baseScore = matchOnBaseString ? matchOnBaseString.score : 0;

      if (string.toLowerCase().startsWith(queryWithoutBang)) {
        baseScore += 1;
      }

      // 4. If the query is only a bang, we want to artifically boost all items to show them in the list.
      const bangScore = isBangOnly ? 1 : 0;
      // 5. Compute an average of our scores.
      const score = [baseScore, bangScore].reduce((a, b) => a + b, 0) / 2;

      return {
        item: {
          ...item,
          score: showScores ? score : undefined,
        },
        score,
      };
    })
    .filter(({score}) => score > 0)
    .sort((a, b) => b.score - a.score)
    .map((r) => r.item)
    .value();
}
